// The recorder as a screen sees it: the page's one engine
// (recorderEngine.ts), its state and its actions. Screens come and go; the
// recording does not (RecorderProvider binds the engine to the signed-in
// workspace above every route).

import { useSyncExternalStore } from "react";

import type { RecorderSnapshot, StartOptions } from "./recorderEngine";
import { recorder } from "./recorderMedia";

export type { RecorderPhase, RecorderSnapshot } from "./recorderEngine";
export { isStandalone, micErrorMessage, recordingSupported } from "./recorderMedia";

export interface RecorderControls extends RecorderSnapshot {
  /** Straight from the tap: the microphone is asked for before anything else. */
  start: (o: StartOptions) => Promise<void>;
  stop: () => Promise<void>;
  /** Straight from the tap, like start. */
  /** `callSound`: carry on as the screen's choice says (left out: as the take was recorded). */
  carryOn: (how?: { callSound?: boolean }) => Promise<void>;
  finishNow: () => void;
  /** Stop the live take and finish every held one now (a sign-out that asked first). */
  stopAndUpload: () => Promise<void>;
  dismissOffer: () => void;
  /** A call recording whose share ended: share again (straight from the tap). */
  reshareCallSound: () => Promise<"ok" | "cancelled" | "no_sound" | "blocked" | "none">;
  reset: () => void;
  localAudio: (takeId?: string) => Promise<{ blob: Blob; partial: boolean } | null>;
  /** Forget audio the server would not add, once it is saved as a file. */
  discardKept: (takeId: string) => void;
}

export function useRecorder(): RecorderControls {
  const eng = recorder();
  const snap = useSyncExternalStore(eng.subscribe, eng.getSnapshot, eng.getSnapshot);
  return {
    ...snap,
    start: (o) => eng.start(o),
    stop: () => eng.stop(),
    carryOn: (how) => eng.carryOn(how),
    finishNow: () => eng.finishNow(),
    stopAndUpload: () => eng.stopAndUpload(),
    dismissOffer: () => eng.dismissOffer(),
    reshareCallSound: () => eng.reshareCallSound(),
    reset: () => eng.reset(),
    localAudio: (takeId) => eng.localAudio(takeId),
    discardKept: (takeId) => eng.discardKept(takeId),
  };
}
